@let config = config$ | async;

<div blazeVirtualScrollableElement class="scrolling-element">
  @if (config && isVirtualScrollRegistered) {
    <table #table class="blaze-table">
      <!-- #region table header-->
      <tr
        class="table-row table-row-header"
        cdkDropList
        cdkDropListOrientation="horizontal"
        (cdkDropListDropped)="moveColumn($event)"
        (cdkDropListSorted)="setDraggedColumnsPreview($event)"
        [style.min-width.px]="columns.length * cellWidthPx"
      >
        @for (
          col of columns;
          track columnTrackByFn(index, col);
          let index = $index;
          let isFirst = $first;
          let isLast = $last
        ) {
          <th
            #columnHeaders
            [attr.data-colId]="col.id"
            [attr.data-columnName]="col.name"
            class="cell col-header"
            [class.hidden]="isHiddenColumn(col)"
            [class.first]="isFirst"
            [class.!relative]="isDragging"
            [class.inline]="col.isInline || col.columnType === 'Link'"
            cdkDrag
            [cdkDragDisabled]="!isDraggable"
            cdkDragBoundary=".table-row-header"
            [class.dragged]="draggedColumn?.name === col.name"
            (mouseenter)="setMouseOverCol(col)"
            (mouseleave)="setMouseOverCol(null)"
            (cdkDragStarted)="calculatePreviewHeight(); setIsDragging(true)"
            (cdkDragMoved)="setDraggedColumn(col)"
            (cdkDragEnded)="setDraggedColumn(null); setIsDragging(false)"
          >
            @if (isFirst && !isDragging) {
              <div class="select-all">
                <blaze-checkbox [checked]="isAllSelected()" (change)="onAllRowsSelection($event)" />
              </div>
            }
            <div
              #addColumnTrigger
              style="position: absolute; bottom: 0; left: 0"
              (click)="openColumnEditorPanel(addColumnTrigger)"
            ></div>
            <mat-icon
              class="col-type-icon"
              [svgIcon]="getColumnTypeIcon(col)"
              cdkDragHandle
              [class.is-draggable]="isDraggable"
              [class.is-dragged]="isDragging"
              (mousedown)="setIsDragging(true)"
              (mouseup)="setIsDragging(false)"
              (mouseenter)="setDraggedColumn(col)"
              (mouseleave)="setDraggedColumn(null)"
            />
            <div class="col-name ml-10 text-13">
              @if (config.displayColumn === col.name) {
                <span class="display-column-icon" role="img" aria-label="Display column">*</span>
              }
              <input
                #columnName
                [attr.data-column]="col.name"
                type="text"
                (focus)="blazeTableStore.setSourceColumn(col)"
                (blur)="editColumnName(col, $event)"
                [value]="col.name"
                [readOnly]="isProtectedColumn(col)"
                autocomplete="off"
                class="input-string"
              />
            </div>
            @let columnReferences = getColumnReferences(col);

            <!-- Action Menu -->
            <div
              data-cy="BlazeTable:Column:ActionsTrigger"
              [matMenuTriggerFor]="colActionsMenu"
              class="col-menu-icon rounded bg-base-light"
            >
              <mat-icon svgIcon="icon_chevron_down_8" />
            </div>
            <mat-menu
              #colActionsMenu="matMenu"
              [xPosition]="isLast ? 'before' : 'after'"
              class="col-action-menu bd-menu"
            >
              <ng-template matMenuContent>
                <blaze-table-column-actions-menu
                  (actionSelected)="startColumnAction($event, index)"
                  [column]="col"
                  [columnsCount]="deletableColumnsCount"
                  [isProtectedColumn]="isProtectedColumn(col)"
                  [isDisplayColumnProtected]="isDisplayColumnProtected()"
                  [notEditable]="isReadOnly"
                  [referencesCount]="columnReferences.length"
                />
              </ng-template>
            </mat-menu>
          </th>
        }
        @if (isRowManagementEnabled) {
          <th
            #th
            (click)="addTableColumn(th)"
            (keydown.enter)="addTableColumn(th)"
            class="add-button add-column"
            data-cy="BlazeTable:addColumn"
          >
            <mat-icon svgIcon="icon_add_32" />
          </th>
        }
      </tr>
      <!-- #endregion table header-->
      @if (rows.length) {
        <cdk-virtual-scroll-viewport
          #rowVirtualScrollViewport
          class="row-virtual-scroll-viewport"
          blazeRowAutosize
          [enableAutosize]="true"
          [items]="rows"
          [itemSize]="40"
        >
          <!-- Rows with data which existed when we opened the table for editing -->
          <ng-container
            *cdkVirtualFor="
              let row of rows;
              let index = index;
              let isLast = last;
              let isFirst = first;
              trackBy: rowTrackByFn
            "
            [ngTemplateOutlet]="rowToDisplayTpl"
            [ngTemplateOutletContext]="{
              index: index,
              row: row,
              isLast: isLast,
              isNewRow: !row?._id,
              isSelected: selectedRows[row?._id ?? index],
              isFooter: false,
              isFirst: isFirst,
            }"
          />
        </cdk-virtual-scroll-viewport>
      }
      @if (isLoading) {
        <tr
          class="sticky left-0 min-h-[1px] w-full flex items-center justify-center"
          style="max-width: calc(100vw - 48px)"
        >
          @if (hasMoreRows) {
            <div class="h-[30px]">
              <blaze-element-loading-indicator [isVisible]="isLoading" [fadeEnabled]="false" />
            </div>
          }
        </tr>
      }
      <!-- Add new row -->
      @if (!newRow && isRowManagementEnabled) {
        <tr
          #addRowTpl
          class="table-row table-row-footer"
          (mouseover)="setMouseOverRow(undefined)"
          [style.width.px]="columns.length * cellWidthPx"
        >
          @for (col of columns; track col; let isFirst = $first) {
            <td
              class="cell add-row-cell"
              [class.has-add-row-btn]="isFirst"
              [class.!relative]="isDragging"
            >
              @if (isFirst) {
                <div
                  tabindex="0"
                  class="add-row"
                  (click)="addTableRow()"
                  (keydown.enter)="addTableRow()"
                >
                  <mat-icon class="add-row-icon" svgIcon="icon_add_32" />
                  <div class="ml-10">Row</div>
                </div>
              }
            </td>
          }
          <td class="row-button empty"></td>
        </tr>
      } @else if (isRowManagementEnabled) {
        <ng-container
          [ngTemplateOutlet]="rowToDisplayTpl"
          [ngTemplateOutletContext]="{
            index: totalCount,
            row: newRow,
            isLast: true,
            isNewRow: true,
            displayedRowsLimit: totalCount + 1,
            isSelected: selectedRows[newRow?._id ?? totalCount + 1],
            isFooter: true,
          }"
        />
      }
    </table>
  }
</div>
<mat-menu
  #addColumnMenu="matMenu"
  class="add-column-menu visible-overflow"
  (closed)="onMenuClosed()"
>
  <ng-template matMenuContent>
    @if (columnEditorVisible$ | async) {
      <blaze-column-editor-panel
        [tableConfig$]="config$"
        [tableId]="config?.id"
        [rowsCount]="rows.length"
        [allowedConversions]="allowedConversions"
        (addField)="processConfiguredColumn($event)"
        (click)="$event.stopPropagation()"
        (closePanel)="closeAddColumnDialog()"
        (keydown.tab)="$event.stopPropagation()"
      />
    }
  </ng-template>
</mat-menu>

@if (progressDialogConfig$$ | async) {
  <div
    class="action-backdrop fixed z-[1010] w-full h-full top-0 left-0 right-0 bottom-0 bg-base-dark opacity-10"
  ></div>
}

<ng-template
  #rowToDisplayTpl
  let-row="row"
  let-index="index"
  let-isNewRow="isNewRow"
  let-isSelected="isSelected"
  let-isFooter="isFooter"
>
  <tr
    #tableRow
    (mouseover)="setMouseOverRow(index)"
    [class.selected]="isSelected"
    [class.active]="rowFocusIndex === index"
    [class.add-row]="isNewRow"
    class="table-row"
    [attr.aria-rowindex]="index + 1"
    [attr.data-index]="index"
    [class.table-row-footer]="isFooter"
    [class.table-new-row-footer]="isFooter"
    [style.width.px]="isFooter ? footerRowWidth : undefined"
  >
    @for (
      col of stickyColumns;
      track columnTrackByFn(columnIndex, col);
      let isFirst = $first;
      let columnIndex = $index
    ) {
      @let cellError = getRowError(row, col, index, isFooter);
      @let cellValue = getCellValue(index, col.name, isFooter);
      <td
        #cell
        [class.hidden]="isHiddenColumn(col)"
        [class.error]="cellError"
        [class.first]="isFirst"
        [class.!relative]="isDragging"
        [class.dragged]="draggedColumn?.name === col.name"
        [attr.data-column]="col.name"
        [attr.aria-colindex]="columnIndex + 1"
        [attr.aria-rowindex]="index"
        class="cell content-cell group"
        tabindex="0"
        (click)="cellClick($event, col, index, cell, row, cellValue, cellError, isFooter, isFirst)"
        (keydown.enter)="
          cellClick($event, col, index, cell, row, cellValue, cellError, isFooter, isFirst)
        "
      >
        @if (isFirst) {
          <div
            class="row-num"
            (mouseenter)="!isFooter && (hoveredRowCheck = index)"
            (mouseleave)="hoveredRowCheck = undefined"
            (click)="$event.stopPropagation()"
            (keydown.enter)="$event.stopPropagation()"
          >
            @if (hoveredRowCheck === index || isSelected) {
              <blaze-checkbox [checked]="isSelected" (change)="onRowSelection(index, row?._id)" />
            } @else {
              {{ index + 1 }}
            }
          </div>
        }
        <ng-container
          [ngTemplateOutlet]="fieldTpl"
          [ngTemplateOutletContext]="{
            rowIndex: index,
            value: cellValue,
            column: col,
            isFooter: isFooter,
            isFirst: isFirst,
            error: cellError,
            row: row,
            cell: cell,
            readonly:
              col.isProtected ||
              col.isInternal ||
              col.isInline ||
              col.isComputed ||
              config?.isChangeTrackingTable,
          }"
        />
      </td>
    }
    @if (virtualScrollVisibleColumns.length > 0) {
      <cdk-virtual-scroll-viewport
        class="column-virtual-scroll-viewport"
        [itemSize]="cellWidthPx"
        orientation="horizontal"
        [style.width.px]="virtualScrollVisibleColumns.length * cellWidthPx"
      >
        <td
          #cell
          [attr.aria-colindex]="colIndex + 1 + stickyColumns.length"
          [attr.aria-rowindex]="index"
          [style.min-height.px]="cellHeights[index]"
          *cdkVirtualFor="
            let col of virtualScrollVisibleColumns;
            let colIndex = index;
            trackBy: columnTrackByFn
          "
          [class.hidden]="isHiddenColumn(col)"
          [class.error]="getRowError(row, col, index, isFooter)"
          [class.dragged]="draggedColumn?.name === col.name"
          [attr.data-column]="col.name"
          class="cell content-cell group"
          [tabindex]="col.isComputed || col.isInline || col.isInternal || col.isProtected ? -1 : 0"
          (click)="
            cellClick(
              $event,
              col,
              index,
              cell,
              row,
              getCellValue(index, col.name, isFooter),
              getRowError(row, col, index, isFooter),
              isFooter,
              false
            )
          "
          (keydown.enter)="
            cellClick(
              $event,
              col,
              index,
              cell,
              row,
              getCellValue(index, col.name, isFooter),
              getRowError(row, col, index, isFooter),
              isFooter,
              false
            )
          "
        >
          <ng-container
            [ngTemplateOutlet]="fieldTpl"
            [ngTemplateOutletContext]="{
              rowIndex: index,
              value: getCellValue(index, col.name, isFooter),
              column: col,
              isFooter: isFooter,
              isFirst: false,
              error: getRowError(row, col, index, isFooter),
              row: row,
              cell: cell,
              readonly:
                col.isProtected ||
                col.isInternal ||
                col.isInline ||
                col.isComputed ||
                config?.isChangeTrackingTable,
            }"
          />
        </td>
      </cdk-virtual-scroll-viewport>
    }
    <td
      [class.visible]="index === mouseOverRowIndex || index === rowFocusIndex"
      [class.invisible]="!isRowManagementEnabled"
      class="row-button"
    >
      @if (!config?.isChangeTrackingTable) {
        <button class="delete-row-button" (click)="deleteTableRow($event, index, isFooter)">
          <mat-icon svgIcon="icon_trash_16" />
        </button>
      }
    </td>
  </tr>
</ng-template>

<ng-template
  #fieldTpl
  let-rowIndex="rowIndex"
  let-value="value"
  let-column="column"
  let-isFooter="isFooter"
  let-error="error"
  let-row="row"
  let-cell="cell"
  let-readonly="readonly"
  let-isFirst="isFirst"
>
  @switch (columnsTemplates[column.id]) {
    @case ('fileTpl') {
      <div
        class="drop-files"
        [blazeDropFile]="(!value && !column.multipleValues) || column.multipleValues"
        (fileDropped)="
          fileDropped($event, column, rowIndex, cell, row, value, error, isFooter, isFirst)
        "
      >
        <div class="files-cell">
          @for (file of value | toArray; track file) {
            @if (file) {
              @let fileThumb = file || '' | getFileThumb;
              @let fileName = file || '' | getFileName;
              <img
                class="h-[34px]"
                [src]="fileThumb"
                [blazeTooltip]="fileName"
                [alt]="fileName"
                (click)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'file-clicked',
                    file: file,
                  })
                "
                (keydown.enter)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'file-clicked',
                    file: file,
                  })
                "
              />
            }
          }
        </div>
        @if (!readonly && (column.multipleValues || !value)) {
          <div
            class="add-cell-value-button"
            (click)="
              cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                type: 'file-add',
              })
            "
            (keydown.enter)="
              cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                type: 'file-add',
              })
            "
          >
            <button blazeIconButton="secondary">
              <mat-icon svgIcon="icon_add_32" />
            </button>
            @if (!value?.length) {
              <span>Click + or drop file here</span>
            }
          </div>
        }
      </div>
    }
    @case ('secureTpl') {
      {{ value | formatValue: 'secureText' }}
    }
    @case ('durationTpl') {
      {{ value | formatValue: 'duration' : column.format || { durationFormat: 'h:mm' } }}
    }
    @case ('geopointTpl') {
      {{ value | formatValue: 'geopoint' }}
    }
    @case ('currencyTpl') {
      {{
        value
          | formatValue
            : 'currency'
            : { currency: column.format?.currencySymbol, precision: column.precision || 0 }
      }}
    }
    @case ('numberTpl') {
      {{ value | formatValue: 'number' : { useGrouping: false, precision: column.precision || 0 } }}
    }
    @case ('percentageTpl') {
      {{
        value | formatValue: 'percentage' : { useGrouping: false, precision: column.precision || 0 }
      }}
    }
    @case ('datetimeTpl') {
      {{ value | formatValue: 'datetime' : { dateTimeFormat: 'yyyy-MM-dd hh:mm A' } }}
    }
    @case ('dateTpl') {
      {{ value | formatValue: 'date' : { dateFormat: 'yyyy-MM-dd' } }}
    }
    @case ('timeTpl') {
      {{ value | formatValue: 'time' }}
    }
    @case ('phoneNumberTpl') {
      {{
        value
          | formatValue
            : 'phoneNumber'
            : { asYouTypeFormatter: true, culture: column.format.culture }
      }}
    }
    @case ('richTextTpl') {
      @if (value) {
        <span class="rich-text-cell" [innerHTML]="value | formatValue: 'richText'"></span>
      }
    }
    @case ('tagTpl') {
      <div class="tag-cell">
        @let arrayValue = value | toArray | sort;
        @for (val of arrayValue; track val) {
          <span
            class="data-chip tag-chip"
            [style]="column.tagDefinition?.items | getTagStyling: val"
          >
            <span class="tag-text">{{ val }}</span>
            @if (!readonly) {
              <mat-icon
                svgIcon="icon_close_8"
                (click)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'tag-remove',
                    tag: val,
                  })
                "
                (keydown.enter)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'tag-remove',
                    tag: val,
                  })
                "
              />
            }
          </span>
        }
      </div>
      @if (!readonly) {
        <mat-icon
          class="tag-adorn"
          svgIcon="icon_chevron_down_16"
          (click)="
            cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
              type: 'tag-add',
            })
          "
          (keydown.enter)="
            cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
              type: 'tag-add',
            })
          "
        />
      }
    }
    @case ('linkTpl') {
      <div class="link-cell">
        @let arrayValue = value | toArray;
        @for (val of arrayValue; track val) {
          <span class="data-chip link-chip">
            {{ val.display }}
            @if (!column.isInline && !readonly) {
              <mat-icon
                svgIcon="icon_close_8"
                (click)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'link-remove',
                    link: val,
                  })
                "
                (keydown.enter)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'link-remove',
                    link: val,
                  })
                "
              />
            }
          </span>
        }
        @if (!readonly && (!arrayValue.length || (column | isMultiValueLink)) && !column.isInline) {
          <div
            class="link-menu-trigger"
            (click)="
              cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                type: 'link-add',
              })
            "
            (keydown.enter)="
              cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                type: 'link-add',
              })
            "
          >
            <mat-icon svgIcon="icon_add_32" />
          </div>
        }
      </div>
    }
    @case ('timezoneTpl') {
      @let val = value | formatTimeZone | async;
      @if (val) {
        <span class="data-chip timezone-chip">
          {{ val }}
          @if (!readonly) {
            <mat-icon
              svgIcon="icon_close_8"
              (click)="
                cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                  type: 'timezone-remove',
                  timezone: value,
                })
              "
              (keydown.enter)="
                cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                  type: 'timezone-remove',
                  timezone: value,
                })
              "
            />
          }
        </span>
      }
      @if (!readonly) {
        <mat-icon
          class="timezone-adorn"
          svgIcon="icon_chevron_down_16"
          (click)="
            cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
              type: 'timezone-add',
            })
          "
          (keydown.enter)="
            cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
              type: 'timezone-add',
            })
          "
        />
      }
    }
    @case ('zipcodeTpl') {
      {{ value | formatValue: 'zipcode' }}
    }
    @case ('imageTpl') {
      <div
        class="drop-files"
        [blazeDropFile]="(!value && !column.multipleValues) || column.multipleValues"
        (fileDropped)="
          fileDropped($event, column, rowIndex, cell, row, value, error, isFooter, isFirst)
        "
      >
        <div class="images-cell">
          @for (image of value | toArray; track image) {
            @if (image) {
              @let fileName = image | getFileName;
              <img
                class="h-[34px] w-[28px] object-cover"
                [src]="image"
                [alt]="fileName"
                [blazeTooltip]="fileName"
                (click)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'image-clicked',
                    file: image,
                  })
                "
                (keydown.enter)="
                  cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                    type: 'image-clicked',
                    file: image,
                  })
                "
              />
            }
          }
        </div>
        @if (!readonly && (column.multipleValues || !value)) {
          <div
            class="add-cell-value-button"
            (click)="
              cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                type: 'image-add',
              })
            "
            (keydown.enter)="
              cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
                type: 'image-add',
              })
            "
          >
            <button blazeIconButton="secondary">
              <mat-icon svgIcon="icon_add_32" />
            </button>
            @if (!value?.length) {
              <span>Click + or drop file here</span>
            }
          </div>
        }
      </div>
    }
    @case ('booleanTpl') {
      <blaze-checkbox
        [checked]="value"
        (click)="
          cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
            type: 'boolean-switch',
          })
        "
        (keydown.enter)="
          cellClick($event, column, rowIndex, cell, row, value, error, isFooter, isFirst, {
            type: 'boolean-switch',
          })
        "
      />
    }
    @default {
      <span class="text-cell">{{ value | formatValue: 'text' }}</span>
    }
  }

  @if (error) {
    <div class="error-icon">
      <mat-icon
        svgIcon="warning"
        blazeTooltip="{{ error }}"
        matTooltipClass="error-tooltip"
        matTooltipPosition="above"
      />
    </div>
  }

  @if (
    readonly &&
    columnsTemplates[column.id] !== 'secureTpl' &&
    value !== '' &&
    value !== null &&
    value !== undefined
  ) {
    <button
      blazeIconButton
      (click)="copy($event, columnsTemplates[column.id], value)"
      class="copy-value group-hover:flex"
    >
      <mat-icon svgIcon="icon_duplicate_16" />
    </button>
  }
</ng-template>

<ng-template #images />
